import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { EASE } from "@/lib/motion";
import { TASK_CATEGORIES, CAPABILITIES, EVIDENCE_TYPES, VERIFICATION_METHODS } from "@/data/demoTasks";
import { PreviewTag } from "@/components/kit";

const DEADLINES = [
    { label: "2 HOURS", sec: 2 * 3600 },
    { label: "6 HOURS", sec: 6 * 3600 },
    { label: "12 HOURS", sec: 12 * 3600 },
    { label: "24 HOURS", sec: 24 * 3600 },
    { label: "72 HOURS", sec: 72 * 3600 },
];

const STEPS = ["DETAILS", "EVIDENCE & SETTLEMENT", "REVIEW"];
const inputCls =
    "w-full border border-white/10 bg-[#040811] px-4 py-3 font-mono text-xs text-ink placeholder:text-mute/50 transition-colors duration-300 focus:border-[#1677FF] focus:outline-none";
const labelCls = "label-tech mb-2 block";

const EMPTY = {
    name: "",
    description: "",
    reward: "",
    category: TASK_CATEGORIES[0],
    capability: CAPABILITIES[0],
    deadline: DEADLINES[2],
    evidence: [],
    method: VERIFICATION_METHODS[0],
    settlementAddress: "",
};

export const CreateTaskModal = ({ open, onClose, onCreate }) => {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(EMPTY);
    const [created, setCreated] = useState(false);

    useEffect(() => {
        if (open) {
            setStep(0);
            setForm(EMPTY);
            setCreated(false);
        }
    }, [open]);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleEvidence = (t) =>
        set("evidence", form.evidence.includes(t) ? form.evidence.filter((e) => e !== t) : [...form.evidence, t]);

    const canNext = step === 0 ? form.name.trim() && form.reward : step === 1 ? form.evidence.length > 0 : true;

    const submit = () => {
        onCreate?.({
            id: String(Math.floor(10500 + Math.random() * 400)),
            title: form.name,
            category: form.category,
            reward: Number(form.reward) || 0,
            capability: form.capability,
            deadlineSec: form.deadline.sec,
            verification: "ACTRA PROOF",
            status: "OPEN",
            method: form.method,
            evidence: form.evidence,
            description: form.description || "No description provided.",
        });
        setCreated(true);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-6"
                    onClick={onClose}
                    data-testid="create-task-modal"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 40, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Create task"
                        className="flex max-h-[92vh] w-full max-w-2xl flex-col border border-white/10 bg-[#07111F]"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-white/[0.08] p-5 md:p-6">
                            <div>
                                <p className="label-tech">TASK MARKET — NEW TASK</p>
                                <h3 className="mt-1 font-display text-xl font-bold uppercase tracking-tightest">CREATE TASK</h3>
                            </div>
                            <button data-testid="create-task-close" onClick={onClose} aria-label="Close" className="border border-white/10 p-2.5 text-mute transition-colors hover:text-ink">
                                <X size={16} />
                            </button>
                        </div>

                        {/* Steps */}
                        <div className="flex items-center gap-2 border-b border-white/[0.08] px-5 py-3 md:px-6">
                            {STEPS.map((s, i) => (
                                <span key={s} className="flex items-center gap-2">
                                    <span
                                        className={`font-mono text-[9px] uppercase tracking-[0.2em] ${
                                            i === step ? "text-[#31D8FF]" : i < step ? "text-ink" : "text-mute/50"
                                        }`}
                                    >
                                        {String(i + 1).padStart(2, "0")} {s}
                                    </span>
                                    {i < STEPS.length - 1 && <span className="h-px w-4 bg-white/15" aria-hidden="true" />}
                                </span>
                            ))}
                        </div>

                        <div className="flex-1 overflow-y-auto p-5 md:p-6" data-testid="create-task-form">
                            {created ? (
                                <div className="py-10 text-center" data-testid="create-task-success">
                                    <span className="mx-auto flex h-14 w-14 items-center justify-center border border-[#31D8FF]/40 bg-[#31D8FF]/10">
                                        <Check size={24} className="text-[#31D8FF]" />
                                    </span>
                                    <h4 className="mt-6 font-display text-2xl font-bold uppercase tracking-tightest">TASK CREATED</h4>
                                    <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-mute">
                                        Submitted to the local protocol preview. No blockchain transaction was sent — task creation
                                        goes on-chain when the ACTRA contract launches on Arc.
                                    </p>
                                    <PreviewTag className="mt-6">PROTOCOL PREVIEW</PreviewTag>
                                    <div className="mt-8">
                                        <button data-testid="create-task-done" onClick={onClose} className="bg-[#1677FF] px-8 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:bg-[#3D8BFF]">
                                            DONE
                                        </button>
                                    </div>
                                </div>
                            ) : step === 0 ? (
                                <div className="space-y-5">
                                    <div>
                                        <label htmlFor="ct-name" className={labelCls}>TASK NAME *</label>
                                        <input id="ct-name" data-testid="create-task-name" className={inputCls} placeholder="Inspect API infrastructure" value={form.name} onChange={(e) => set("name", e.target.value)} />
                                    </div>
                                    <div>
                                        <label htmlFor="ct-desc" className={labelCls}>DESCRIPTION</label>
                                        <textarea id="ct-desc" data-testid="create-task-description" rows={3} className={`${inputCls} resize-none`} placeholder="What should the agent accomplish?" value={form.description} onChange={(e) => set("description", e.target.value)} />
                                    </div>
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="ct-reward" className={labelCls}>REWARD (USDC) *</label>
                                            <input id="ct-reward" data-testid="create-task-reward" type="number" min="1" className={inputCls} placeholder="25" value={form.reward} onChange={(e) => set("reward", e.target.value)} />
                                        </div>
                                        <div>
                                            <label htmlFor="ct-deadline" className={labelCls}>DEADLINE</label>
                                            <select id="ct-deadline" data-testid="create-task-deadline" className={inputCls} value={form.deadline.label} onChange={(e) => set("deadline", DEADLINES.find((d) => d.label === e.target.value))}>
                                                {DEADLINES.map((d) => (
                                                    <option key={d.label} value={d.label}>{d.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="ct-category" className={labelCls}>CATEGORY</label>
                                            <select id="ct-category" data-testid="create-task-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                                                {TASK_CATEGORIES.map((c) => (
                                                    <option key={c} value={c}>{c.toUpperCase()}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="ct-capability" className={labelCls}>REQUIRED CAPABILITY</label>
                                            <select id="ct-capability" data-testid="create-task-capability" className={inputCls} value={form.capability} onChange={(e) => set("capability", e.target.value)}>
                                                {CAPABILITIES.map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            ) : step === 1 ? (
                                <div className="space-y-6">
                                    <div>
                                        <span className={labelCls}>EVIDENCE REQUIREMENTS * — DEPENDS ON THE TASK</span>
                                        <div className="flex flex-wrap gap-2" data-testid="create-task-evidence">
                                            {EVIDENCE_TYPES.map((t) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    aria-pressed={form.evidence.includes(t)}
                                                    onClick={() => toggleEvidence(t)}
                                                    className={`border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] transition-all duration-200 ${
                                                        form.evidence.includes(t)
                                                            ? "border-[#1677FF] bg-[#1677FF]/15 text-ink"
                                                            : "border-white/10 bg-[#0A1627] text-mute hover:border-[#1677FF]/50"
                                                    }`}
                                                >
                                                    {t}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="ct-method" className={labelCls}>VERIFICATION METHOD</label>
                                        <select id="ct-method" data-testid="create-task-method" className={inputCls} value={form.method} onChange={(e) => set("method", e.target.value)}>
                                            {VERIFICATION_METHODS.map((m) => (
                                                <option key={m} value={m}>{m}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="ct-address" className={labelCls}>SETTLEMENT ADDRESS (OPTIONAL)</label>
                                        <input id="ct-address" data-testid="create-task-address" className={inputCls} placeholder="0x… — defaults to connected wallet at launch" value={form.settlementAddress} onChange={(e) => set("settlementAddress", e.target.value)} />
                                    </div>
                                    <p className="border-l-2 border-[#1677FF]/50 bg-[#0A1627] p-4 text-xs leading-relaxed text-mute">
                                        No blockchain transaction is sent in this preview. Reward stays a local preview value until
                                        the ACTRA contract is live on Arc.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4" data-testid="create-task-review">
                                    {[
                                        ["TASK NAME", form.name || "—"],
                                        ["CATEGORY", form.category.toUpperCase()],
                                        ["REWARD", `${form.reward || 0} USDC`],
                                        ["CAPABILITY", form.capability],
                                        ["DEADLINE", form.deadline.label],
                                        ["EVIDENCE", form.evidence.join(" · ") || "—"],
                                        ["VERIFICATION", form.method],
                                        ["SETTLEMENT", form.settlementAddress || "CONNECTED WALLET (AT LAUNCH)"],
                                    ].map(([k, v]) => (
                                        <div key={k} className="flex items-baseline justify-between gap-6 border-b border-white/[0.06] pb-3">
                                            <span className="label-tech">{k}</span>
                                            <span className="text-right font-mono text-xs text-ink">{v}</span>
                                        </div>
                                    ))}
                                    <PreviewTag className="mt-2">REVIEW — NO TRANSACTION WILL BE SENT</PreviewTag>
                                </div>
                            )}
                        </div>

                        {!created && (
                            <div className="flex items-center justify-between gap-3 border-t border-white/[0.08] p-5 md:p-6">
                                <button
                                    data-testid="create-task-back"
                                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                                    disabled={step === 0}
                                    className="inline-flex items-center gap-2 border border-white/10 px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-mute transition-colors hover:text-ink disabled:opacity-30"
                                >
                                    <ArrowLeft size={12} /> BACK
                                </button>
                                {step < 2 ? (
                                    <button
                                        data-testid="create-task-next"
                                        onClick={() => canNext && setStep((s) => s + 1)}
                                        disabled={!canNext}
                                        className="inline-flex items-center gap-2 bg-[#1677FF] px-6 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#3D8BFF] disabled:opacity-40"
                                    >
                                        CONTINUE <ArrowRight size={12} />
                                    </button>
                                ) : (
                                    <button
                                        data-testid="create-task-submit"
                                        onClick={submit}
                                        className="inline-flex items-center gap-2 bg-[#1677FF] px-6 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#3D8BFF]"
                                    >
                                        CREATE TASK <ArrowRight size={12} />
                                    </button>
                                )}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CreateTaskModal;
